import { CustomProvider } from 'firebase/app-check';
import { env } from './env';

// Extend the Window interface to include Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (
        element: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'before-interactive-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'invisible';
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

interface AppCheckTokenResponse {
  token: string;
  expiresAt: number;
}

/**
 * Wait for the Turnstile script to load
 */
function waitForTurnstile(timeout = 10000): Promise<void> {
  return new Promise((resolve, reject) => {
    // If already loaded, resolve immediately
    if (window.turnstile) {
      resolve();
      return;
    }

    // Set up timeout
    const timeoutId = setTimeout(() => {
      reject(new Error('Turnstile script failed to load within timeout'));
    }, timeout);

    // Poll for Turnstile availability
    const checkInterval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        resolve();
      }
    }, 100); // Check every 100ms
  });
}

/**
 * Get a Turnstile token using the invisible widget
 */
async function getTurnstileToken(): Promise<string> {
  // Wait for Turnstile to be available
  await waitForTurnstile();

  return new Promise((resolve, reject) => {
    // Safari's ITP blocks credentialed cross-origin requests from
    // effectively-zero-size iframes (it treats them as tracking pixels), so
    // the Turnstile challenge handshake gets a 401. We mount the widget in a
    // backdrop+modal that has real layout dimensions, but keep it visually
    // hidden (opacity:0, pointer-events:none) so Chrome's auto-pass case
    // doesn't flash. We only reveal the modal if Turnstile signals that an
    // interactive challenge is about to be shown.
    const backdrop = document.createElement('div');
    backdrop.style.cssText = [
      'position: fixed',
      'inset: 0',
      'background: rgba(0, 0, 0, 0.5)',
      'z-index: 2147483647',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'opacity: 0',
      'pointer-events: none',
      'transition: opacity 0.15s ease-out',
    ].join(';');

    const modal = document.createElement('div');
    modal.style.cssText = [
      'background: #fff',
      'padding: 24px',
      'border-radius: 8px',
      'box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2)',
      'display: flex',
      'flex-direction: column',
      'align-items: center',
      'gap: 16px',
    ].join(';');

    const label = document.createElement('div');
    label.textContent = "Verifying you're human…";
    label.style.cssText = 'font-size: 14px; color: #111;';

    const widgetContainer = document.createElement('div');

    modal.appendChild(label);
    modal.appendChild(widgetContainer);
    backdrop.appendChild(modal);
    document.body.appendChild(backdrop);

    let widgetId: string | null = null;
    let settled = false;

    const revealModal = () => {
      backdrop.style.opacity = '1';
      backdrop.style.pointerEvents = 'auto';
    };

    const cleanup = () => {
      if (settled) return;
      settled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (error) {
          console.debug('Turnstile widget already removed:', error);
        }
      }
      if (backdrop.parentNode) {
        document.body.removeChild(backdrop);
      }
    };

    try {
      if (!window.turnstile || !env.VITE_TURNSTILE_SITE_KEY) {
        throw new Error('Turnstile not available or site key missing');
      }

      widgetId = window.turnstile.render(widgetContainer, {
        sitekey: env.VITE_TURNSTILE_SITE_KEY,
        size: 'compact',
        callback: (token: string) => {
          cleanup();
          resolve(token);
        },
        'error-callback': () => {
          cleanup();
          reject(new Error('Turnstile verification failed'));
        },
        'before-interactive-callback': () => {
          revealModal();
        },
      });
    } catch (error) {
      console.log('Error rendering Turnstile:', error);
      cleanup();
      reject(error);
    }
  });
}

/**
 * Exchange Turnstile token for Firebase App Check token via backend
 */
async function exchangeTokenWithBackend(
  turnstileToken: string,
): Promise<AppCheckTokenResponse> {
  const apiBaseUrl = '/api';
  const response = await fetch(`${apiBaseUrl}/app-check/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      turnstileToken,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    console.log('error in exchangeTokenWithBackend', error);
    throw new Error(
      error.message || `Failed to get App Check token: ${response.statusText}`,
    );
  }

  const data: AppCheckTokenResponse = await response.json();
  return data;
}

/**
 * Custom App Check provider using Cloudflare Turnstile
 * Following Firebase documentation pattern
 */
export const appCheckCustomProvider = new CustomProvider({
  getToken: () => {
    return new Promise((resolve, reject) => {
      // In development mode, return a debug token
      if (env.VITE_NODE_ENV === 'development') {
        console.log('Development mode: Using debug App Check token');
        resolve({
          token: 'debug-token',
          expireTimeMillis: Date.now() + 3600000, // 1 hour
        });
        return;
      }

      if (!env.VITE_TURNSTILE_SITE_KEY) {
        reject(new Error('VITE_TURNSTILE_SITE_KEY is not configured'));
        return;
      }

      // Get Turnstile token and exchange for App Check token
      getTurnstileToken()
        .then((turnstileToken) => exchangeTokenWithBackend(turnstileToken))
        .then((appCheckToken) => {
          resolve({
            token: appCheckToken.token,
            expireTimeMillis: appCheckToken.expiresAt * 1000, // Convert seconds to milliseconds
          });
        })
        .catch((error) => {
          console.error('Error getting App Check token:', error);
          reject(error);
        });
    });
  },
});
